import {action, computed, makeObservable, observable, reaction, runInAction} from "mobx";
import {account, accountUpdates, accountUuid, syncData, syncHandler} from "../model/index.js";
import app from "../app.js";
import {cleanUp, loadObject, prepareForUpdate} from "../utils/index.js";
import {outSyncHandler, Sync} from "../sync/index.js";
import {Store} from "./index.js";
import {fetchLockOptions} from "./common.js";
import AsyncLock from "async-lock";

export class AccountStore implements Store {
	_accounts: Record<accountUuid, account> = {};
	_fetchLock = new AsyncLock(fetchLockOptions);

	constructor() {
		makeObservable(this, {
			me:                computed,
			_accounts:         observable,
			update:            action,
			integrationSignup: action,
			_clear:            action,
			_load:             action,
			_remove:           action
		});
	}

	_clear() {
		this._accounts = {};
	}

	_refresh() {
		(Object.keys(this._accounts) as accountUuid[])
			.forEach(uuid => this._fetch(uuid, true));
	}

	get me(): account | undefined {
		if (!app.accountUuid)
			return undefined;

		return this.byUuid(app.accountUuid);
	}

	byUuid(uuid: accountUuid): account | undefined {
		// noinspection JSIgnoredPromiseFromCall
		this._fetch(uuid);

		return this._accounts[uuid];
	}

	/**
	 * Load the current user's account from the backend even if we already have it in memory.
	 */
	async refreshMe(): Promise<void> {
		if (!app.accountUuid)
			return;
		await this._fetch(app.accountUuid, true);
	}

	async update(uuid: accountUuid, updates: accountUpdates, ctx?: any): Promise<{re_verification_email_sent: boolean}> {
		if (this._accounts[uuid] === undefined)
			throw new Error(`Account not found: ${uuid}`);

		const original = cleanUp(this._accounts[uuid]) as account;
		const account = Object.assign(prepareForUpdate(this._accounts[uuid]) as account, updates);
		this._load(account);

		try {
			const ret = await app.backend.updateAccount(uuid, updates);
			runInAction(() => {
				if (this._accounts[uuid])
					this._accounts[uuid] = cleanUp(this._accounts[uuid]) as account;
			});
			return ret;
		} catch (e) {
			app.handleBackendError(e, ctx);
			this._load(original);
			throw e;
		}
	}

	async integrationSignup(integration: string, uuid: accountUuid, name: string, email: string, password: string, terms: boolean): Promise<void> {
		const {account} = await app.backend.upgradeTempAccount(integration, uuid, name, email, password, terms);
		this._load(account);
	}

	_isAccountLoaded(uuid: accountUuid): boolean {
		return this._accounts[uuid] !== undefined;
	}

	async _fetch(uuid: accountUuid, force = false) {
		await this._fetchLock.acquire("_fetch:" + uuid, async () => {
			if (!app.isLoggedIn)
				return;
			if (force || !this._isAccountLoaded(uuid))
				this._load(await app.backend.getAccount(uuid));
		});
	}

	_load(account: account) {
		loadObject(this._accounts, account.uuid, account);
	}

	_remove(uuid: accountUuid) {
		delete this._accounts[uuid];
	}

	_setupSyncHandlers(sync: Sync) {
		sync.setHandler(syncHandler.ACCOUNT_MODIFY, ({account}: syncData[syncHandler.ACCOUNT_MODIFY]) =>
			(account.uuid === app.accountUuid || this._isAccountLoaded(account.uuid)) // Is this account loaded (or is it "me")?
			&& this._load(account) // Then load it
		);
	}

	_setupOutSyncHandlers(handler: outSyncHandler) {
		// This is a bit different from other stores: we always send an ACCOUNT_MODIFY for any change,
		// but only for the current user ("me").
		reaction(() => this.me && JSON.parse(JSON.stringify(this.me)), (account: account) =>
			account // Account for `this.me` possibly returning `undefined`
			&& handler(null, syncHandler.ACCOUNT_MODIFY, {account})
		);
	}
}

export default new AccountStore();
